import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    Tooltip,
    useTheme,
    useMediaQuery,
    Chip,
    Avatar,
} from '@mui/material';
import { ChevronLeft, ChevronRight, Delete as DeleteIcon, AllInclusive as AllInclusiveIcon } from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, addMonths, subMonths, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import ScheduleDialog from './ScheduleDialog';
import ScheduleEditDialog from './ScheduleEditDialog';
import { apiClient } from '../contexts/AuthContext';
import { Auxiliaire } from '../types/auxiliaire';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import NoteDialog from './NoteDialog';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import NotesDialog from './NotesDialog';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import AddIcon from '@mui/icons-material/Add';

interface MonthViewProps {
    currentDate: Date;
    onDateChange: (date: Date) => void;
    dialogOpen: boolean;
    setDialogOpen: (open: boolean) => void;
}

interface Schedule {
    id: number;
    auxiliaire_id: number;
    start_date: string;
    end_date: string;
    start_time: string;
    end_time: string;
    is_recurring: boolean;
    recurrence_group_id?: string;
    day_of_week?: number;
    auxiliaire: Auxiliaire;
}

interface NoteItem {
    id: number;
    content: string;
    schedule_id?: number;
    created_at?: string;
}

const MonthView: React.FC<MonthViewProps> = ({ currentDate, onDateChange, dialogOpen, setDialogOpen }) => {
    const [auxiliaires, setAuxiliaires] = useState<Auxiliaire[]>([]);
    const [schedules, setSchedules] = useState<Schedule[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [noteSchedule, setNoteSchedule] = useState<Schedule | null>(null);
    const [notesDialogOpen, setNotesDialogOpen] = useState(false);
    const [notesByScheduleId, setNotesByScheduleId] = useState<Record<number, NoteItem[]>>({});
    const [loadingNotesFor, setLoadingNotesFor] = useState<Record<number, boolean>>({});
    const [creationDate, setCreationDate] = useState<string | null>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const exportRef = useRef<HTMLDivElement>(null);

    const getAuxiliaireColor = (auxiliaireId: number) => {
        const colors = [
            '#B5EAD7', // Soft mint
            '#FFDAC1', // Peach
            '#FFB7B2', // Light coral
            '#C7CEEA', // Soft lavender
            '#E2F0CB', // Light sage
            '#FFE5D9', // Pale pink
            '#B5E6FF', // Sky blue
            '#D4A5A5', // Dusty rose
            '#A2D2FF', // Light blue
            '#FFC8DD', // Baby pink
        ];
        return colors[auxiliaireId % colors.length];
    };

    const fetchSchedules = useCallback(async () => {
        try {
            const formattedDate = format(currentDate, 'yyyy-MM-dd');
            const response = await apiClient.get<Schedule[]>(`/api/schedules/${formattedDate}`);
            setSchedules(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des plannings :', error);
        }
    }, [currentDate]);

    const fetchAuxiliaires = useCallback(async () => {
        try {
            const response = await apiClient.get<Auxiliaire[]>('/api/auxiliaires');
            setAuxiliaires(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des auxiliaires :', error);
        }
    }, []);

    const handleSaveSchedule = async (schedule: Omit<Schedule, 'id' | 'auxiliaire'>) => {
        try {
            await apiClient.post('/api/schedules', schedule);
            await fetchSchedules();
        } catch (error) {
            console.error('Erreur lors de l\'enregistrement du planning :', error);
        }
    };

    const handleEditSchedule = async (schedule: Omit<Schedule, 'id' | 'auxiliaire'>) => {
        try {
            await apiClient.put(`/api/schedules/${selectedSchedule?.id}`, schedule);
            await fetchSchedules();
        } catch (error) {
            console.error('Erreur lors de la mise à jour du planning :', error);
        }
    };

    const getSchedulesForDate = (date: Date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const filteredSchedules = schedules.filter(schedule => {
            const scheduleStartDate = new Date(schedule.start_date);
            const scheduleEndDate = new Date(schedule.end_date);
            const currentDate = new Date(dateStr);
            
            return (
                (currentDate >= scheduleStartDate && currentDate <= scheduleEndDate) ||
                (schedule.is_recurring &&
                    schedule.day_of_week === date.getDay() &&
                    currentDate >= scheduleStartDate)
            );
        });

        // Sort schedules by start date and start time
        return filteredSchedules.sort((a, b) => {
            // First compare start dates
            const dateA = new Date(a.start_date);
            const dateB = new Date(b.start_date);
            if (dateA.getTime() !== dateB.getTime()) {
                return dateA.getTime() - dateB.getTime();
            }
            // If dates are equal, compare start times
            return a.start_time.localeCompare(b.start_time);
        });
    };

    const handleDeleteSchedule = async (scheduleId: number, deleteAllRecurring: boolean = false) => {
        try {
            const url = deleteAllRecurring 
                ? `/api/schedules/${scheduleId}/recurring`
                : `/api/schedules/${scheduleId}`;
            
            await apiClient.delete(url);
            await fetchSchedules();
        } catch (error) {
            console.error('Erreur lors de la suppression du planning :', error);
        }
    };
    useEffect(() => {
        fetchAuxiliaires();
        fetchSchedules();
    }, [fetchAuxiliaires, fetchSchedules]);

    const handleDeleteClick = (schedule: Schedule) => {
        setSelectedSchedule(schedule);
        if (schedule.is_recurring) {
            setDeleteDialogOpen(true);
        } else {
            handleDeleteSchedule(schedule.id);
        }
    };

    // Calendar navigation
    const handlePreviousMonth = () => onDateChange(subMonths(currentDate, 1));
    const handleNextMonth = () => onDateChange(addMonths(currentDate, 1));

    const handleExportPdf = useCallback(async () => {
        if (!exportRef.current) return;
        try {
            // Ensure web fonts are fully loaded before rasterizing
            try {
                const anyDoc = document as any;
                if (anyDoc?.fonts?.ready) {
                    await anyDoc.fonts.ready;
                }
            } catch {}

            const element = exportRef.current;
            // Temporarily apply print-friendly typography for better rasterization
            element.classList.add('pdf-exporting');
            const canvas = await html2canvas(element, {
                // Cast to any to allow options supported by runtime html2canvas
                // while keeping TS satisfied regardless of type definition versions
                ...( { scale: 2, letterRendering: true } as any ),
                backgroundColor: theme.palette.background.default || '#ffffff',
                useCORS: true,
            } as any);
            const imgData = canvas.toDataURL('image/png');
            // Use landscape A4 and fit content to page keeping aspect ratio
            const pdf = new jsPDF('landscape', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
            const imgWidth = canvas.width * ratio;
            const imgHeight = canvas.height * ratio;
            const x = (pageWidth - imgWidth) / 2;
            const y = (pageHeight - imgHeight) / 2;

            pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight, undefined, 'FAST');
            const fileName = `planning_${format(currentDate, 'MMMM_yyyy', { locale: fr })}.pdf`;
            pdf.save(fileName);
        } catch (err) {
            console.error('Erreur lors de la génération du PDF :', err);
        } finally {
            // Revert temporary styles
            exportRef.current?.classList.remove('pdf-exporting');
        }
    }, [currentDate, theme]);

    // Calendar days calculation
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = new Date(monthStart);
    calendarStart.setDate(calendarStart.getDate() - (monthStart.getDay() || 7) + 1);
    const calendarEnd = new Date(monthEnd);
    calendarEnd.setDate(calendarEnd.getDate() + (7 - (monthEnd.getDay() || 7)));
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    const formatTimeDisplay = (time: string) => {
        const [hours, minutes] = time.split(':');
        if (hours === '00' && minutes === '00') {
            return 'Minuit';
        }
        if (hours === '12' && minutes === '00') {
            return 'Midi';
        }
        return time.substring(0, 5);
    };

    const getScheduleTimeDisplay = (schedule: Schedule, currentDate: Date) => {
        const scheduleStartDate = new Date(schedule.start_date);
        const scheduleEndDate = new Date(schedule.end_date);
        const currentDay = new Date(currentDate);
        currentDay.setHours(0, 0, 0, 0);

        // If the schedule spans multiple days
        if (scheduleStartDate.getDate() !== scheduleEndDate.getDate()) {
            if (isSameDay(currentDay, scheduleStartDate)) {
                // On start day, show from start time to midnight
                return `${formatTimeDisplay(schedule.start_time)} - Minuit`;
            } else if (isSameDay(currentDay, scheduleEndDate)) {
                // On end day, show from midnight to end time
                return `00:00 - ${formatTimeDisplay(schedule.end_time)}`;
            } else {
                // On days in between, show full day
                return '00:00 - Minuit';
            }
        }

        // For single day schedules
        return `${formatTimeDisplay(schedule.start_time)} - ${formatTimeDisplay(schedule.end_time)}`;
    };

    const getScheduleDuration = (schedule: Schedule, currentDate: Date) => {
        const scheduleStartDate = new Date(schedule.start_date);
        const scheduleEndDate = new Date(schedule.end_date);
        const currentDay = new Date(currentDate);
        currentDay.setHours(0, 0, 0, 0);

        let startTime = new Date();
        let endTime = new Date();

        if (scheduleStartDate.getDate() !== scheduleEndDate.getDate()) {
            if (isSameDay(currentDay, scheduleStartDate)) {
                startTime = new Date(`${schedule.start_date}T${schedule.start_time}`);
                endTime = new Date(currentDay);
                endTime.setHours(23, 59, 59, 999);
            } else if (isSameDay(currentDay, scheduleEndDate)) {
                startTime = new Date(currentDay);
                startTime.setHours(0, 0, 0, 0);
                endTime = new Date(`${schedule.end_date}T${schedule.end_time}`);
            } else {
                startTime = new Date(currentDay);
                startTime.setHours(0, 0, 0, 0);
                endTime = new Date(currentDay);
                endTime.setHours(23, 59, 59, 999);
            }
        } else {
            startTime = new Date(`${schedule.start_date}T${schedule.start_time}`);
            endTime = new Date(`${schedule.end_date}T${schedule.end_time}`);
        }

        return (endTime.getTime() - startTime.getTime()) / (1000 * 60); // Duration in minutes
    };


    // Render schedule item
    const ScheduleItem = ({ schedule, currentDate }: { schedule: Schedule, currentDate: Date }) => {
        const color = getAuxiliaireColor(schedule.auxiliaire_id);
        const scheduleStartDate = new Date(schedule.start_date);
        const scheduleEndDate = new Date(schedule.end_date);
        const isMultiDay = scheduleStartDate.getDate() !== scheduleEndDate.getDate();
        const isStartDay = isSameDay(currentDate, scheduleStartDate);
        const isEndDay = isSameDay(currentDate, scheduleEndDate);
        const isMiddleDay = isMultiDay && !isStartDay && !isEndDay;

        const startsAtMidnight = (isMultiDay && (isEndDay || isMiddleDay)) || (!isMultiDay && schedule.start_time.startsWith('00:00'));
        // Check if the schedule ends effectively at midnight for the current day
        const endsEffectivelyAtMidnight = (isMultiDay && (isStartDay || isMiddleDay)) || (!isMultiDay && (schedule.end_time.startsWith('00:00') || schedule.end_time.startsWith('23:59')));
        const continuesToNextDay = isMultiDay && !isEndDay; // Schedule continues beyond today

        // Check if the schedule covers the full duration of the current day
        const isFullDayCoverage = startsAtMidnight && endsEffectivelyAtMidnight;

        const duration = getScheduleDuration(schedule, currentDate);
        // Use 100% height if it covers the full day, otherwise calculate based on duration
        const height = isFullDayCoverage ? '100%' : `${Math.max(40, Math.min(10, duration * 0.3))}px`;

        return (
            <Tooltip 
                arrow
                disableInteractive={false}
                placement="top"
                componentsProps={{
                    tooltip: {
                        sx: {
                            p: 0,
                            backgroundColor: 'transparent',
                            boxShadow: 'none'
                        }
                    },
                    arrow: {
                        sx: { color: 'transparent' }
                    }
                }}
                onOpen={async () => {
                    // lazily fetch notes once per schedule
                    if (!notesByScheduleId[schedule.id] && !loadingNotesFor[schedule.id]) {
                        setLoadingNotesFor(prev => ({ ...prev, [schedule.id]: true }));
                        try {
                            const res = await apiClient.get<NoteItem[]>(`/api/schedules/${schedule.id}/notes`);
                            setNotesByScheduleId((prev: Record<number, NoteItem[]>) => ({ ...prev, [schedule.id]: res.data as NoteItem[] }));
                        } catch {}
                        finally {
                            setLoadingNotesFor(prev => ({ ...prev, [schedule.id]: false }));
                        }
                    }
                }}
                title={
                    <Paper elevation={0} sx={{
                        p: 1.25,
                        minWidth: 240,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        background: `linear-gradient(135deg, ${color}33 0%, ${color}88 100%)`,
                        boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
                        backdropFilter: 'blur(6px)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 12, fontWeight: 700, color: '#fff', background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)` }}>
                                {schedule.auxiliaire.name?.[0]?.toUpperCase() || 'A'}
                            </Avatar>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                    {schedule.auxiliaire.name}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
                                    {schedule.auxiliaire.category}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ height: 1, my: 0.75, background: 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(148,163,184,0.4) 50%, rgba(0,0,0,0) 100%)' }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <AccessTimeIcon sx={{ fontSize: 14 }} />
                                <Typography variant="caption">{getScheduleTimeDisplay(schedule, currentDate)}</Typography>
                            </Box>
                            {schedule.is_recurring && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <AllInclusiveIcon sx={{ fontSize: 14 }} />
                                    <Typography variant="caption">Récurrent</Typography>
                                </Box>
                            )}
                            {isMultiDay && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <CalendarMonthIcon sx={{ fontSize: 14 }} />
                                    <Typography variant="caption">
                                        {format(new Date(schedule.start_date), 'd MMM', { locale: fr })} → {format(new Date(schedule.end_date), 'd MMM', { locale: fr })}
                                    </Typography>
                                </Box>
                            )}
                            {notesByScheduleId[schedule.id] && notesByScheduleId[schedule.id].length > 0 && (
                                <Box sx={{ mt: 0.5, p: 1, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.65)', border: '1px solid', borderColor: 'divider' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        <StickyNote2OutlinedIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>Note</Typography>
                                        <Chip size="small" label={`${notesByScheduleId[schedule.id].length}`} sx={{ ml: 1, height: 16 }} />
                                    </Box>
                                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                                        {notesByScheduleId[schedule.id][0].content}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ mt: 1, display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Tooltip title="Ajouter une note">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setNoteSchedule(schedule); setNoteDialogOpen(true); }}>
                                    <NoteAddIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            {notesByScheduleId[schedule.id] && notesByScheduleId[schedule.id].length > 0 && (
                                <Tooltip title="Gérer les notes">
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setNoteSchedule(schedule); setNotesDialogOpen(true); }}>
                                        <EditNoteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Paper>
                }
            >
                <Box
                    className="schedule-item"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSchedule(schedule);
                        setEditDialogOpen(true);
                    }}
                    sx={{
                        position: 'relative',
                        border: `1px solid ${theme.palette.divider}`, // Add subtle border
                        borderRadius: 1, 
                        px: 0.5,
                        pt: 0.5, 
                        pb: 0.5, 
                        mb: 0.5, 
                        background: `linear-gradient(135deg, ${color}66 0%, ${color}CC 100%)`,
                        color: '#333333',
                        transition: 'all 0.2s ease',
                        height: height, // Use the dynamic height
                        boxSizing: 'border-box', // Ensure padding/border are included in height

                        // Overrides for full day coverage (mostly padding/margin)
                        ...(isFullDayCoverage && {
                            pt: 0,
                            pb: 0,
                            mb: 0,
                        }),

                        // Overrides for starting at midnight (stronger effect on top edge)
                        ...(startsAtMidnight && { 
                            pt: 0, // Ensure padding is 0
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            borderTop: 'none', // Remove top border
                        }),

                        // Overrides for continuing to next day (stronger effect on bottom edge)
                        ...(continuesToNextDay && { 
                            pb: 0, // Ensure padding is 0
                            mb: 0, // Ensure margin is 0
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                            borderBottom: 'none', // Remove bottom border
                        }),

                        '&:hover': {
                            transform: 'translateY(-1px) scale(1.01)',
                            boxShadow: '0 6px 14px rgba(0,0,0,0.15)'
                        },
                        ...(isMultiDay && {
                            background: isStartDay 
                                ? `linear-gradient(to bottom, ${color}66, ${color}CC)`
                                : isEndDay
                                ? `linear-gradient(to top, ${color}66, ${color}CC)`
                                : `linear-gradient(180deg, ${color}80, ${color}80)`
                        }),
                        cursor: 'pointer',
                    }}
                >
                    {(isStartDay || !isMultiDay) && (
                        <IconButton
                            className="hide-on-pdf schedule-action"
                            size="small"
                            aria-label={`Supprimer le créneau de ${schedule.auxiliaire.name}`}
                            sx={{
                                position: 'absolute',
                                right: 2,
                                top: 2,
                                color: '#333333',
                                padding: '2px',
                                '&:hover': {
                                    opacity: 1,
                                    backgroundColor: 'rgba(0, 0, 0, 0.12)'
                                },
                                // Keep it reachable by keyboard even while hidden.
                                '&:focus-visible': { opacity: 1 },
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(schedule);
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                    {isStartDay && (
                        <>
                            <Typography noWrap variant="caption" sx={{ fontWeight: 600, display: 'block', pr: 4, fontSize: '10px', color: '#333333' }}>
                                {schedule.auxiliaire.name}
                            </Typography>
                            <Typography noWrap variant="caption" sx={{ display: 'block', pr: 4, opacity: 0.9, fontSize: '9px', color: '#333333' }}>
                                {getScheduleTimeDisplay(schedule, currentDate)}
                            </Typography>
                            {schedule.is_recurring && (
                                <AllInclusiveIcon 
                                    className="hide-on-pdf"
                                    sx={{ 
                                        position: 'absolute', 
                                        right: 30, 
                                        top: 2, 
                                        fontSize: '12px',
                                        color: '#333333',
                                        opacity: 0.7
                                    }} 
                                />
                            )}
                        </>
                    )}
                    {/* Continuation of an overnight shift. It still needs to name
                        the carer: with a 20:00-08:00 rota, half the calendar is
                        continuation blocks, and a bare time says nothing about
                        who is actually there. */}
                    {isMultiDay && !isStartDay && (
                        <Box sx={{ pr: 4 }}>
                            <Typography
                                noWrap
                                variant="caption"
                                sx={{ fontWeight: 600, display: 'block', fontSize: '10px', color: '#333333', opacity: 0.75 }}
                            >
                                <Box component="span" sx={{ opacity: 0.7, mr: 0.3 }}>↳</Box>
                                {schedule.auxiliaire.name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {notesByScheduleId[schedule.id] && notesByScheduleId[schedule.id].length > 0 && (
                                    <StickyNote2OutlinedIcon sx={{ fontSize: 10, opacity: 0.6, color: '#333333' }} />
                                )}
                                <Typography
                                    noWrap
                                    variant="caption"
                                    sx={{ fontSize: '9px', color: '#333333', opacity: 0.85 }}
                                >
                                    {getScheduleTimeDisplay(schedule, currentDate)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Tooltip>
        );
    };

    return (
        <Box sx={{ width: '100%', height: '100%', p: 2 }}>
            {/* Header */}
            <Box ref={exportRef}>
                <Paper elevation={0} sx={{ 
                    display: 'flex', 
                    justifyContent: { xs: 'center', sm: 'space-between' },
                    alignItems: 'center', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 },
                    mb: 3,
                    p: 2,
                    backgroundColor: 'background.default',
                    borderRadius: 2
                }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                            onClick={handlePreviousMonth} 
                            startIcon={<ChevronLeft />}
                            sx={{ 
                                borderRadius: 2,
                                minWidth: 100,
                                '&:hover': { backgroundColor: 'action.hover' }
                            }}
                        >
                            Précédent
                        </Button>
                        <Button 
                            onClick={handleNextMonth} 
                            endIcon={<ChevronRight />}
                            sx={{ 
                                borderRadius: 2,
                                minWidth: 100,
                                '&:hover': { backgroundColor: 'action.hover' }
                            }}
                        >
                            Suivant
                        </Button>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Poppins, Inter, sans-serif' }}>
                        {format(currentDate, 'MMMM yyyy', { locale: fr })}
                    </Typography>
                    <Box>
                        <Button 
                            onClick={() => { setCreationDate(null); setDialogOpen(true); }}
                            startIcon={<AddIcon />}
                            variant="contained"
                            sx={{ borderRadius: 2 }}
                        >
                            Ajouter un créneau
                        </Button>
                    </Box>
                </Paper>

                {/* Calendar Grid */}
                <Paper elevation={1} sx={{ 
                    borderRadius: 2,
                    overflow: 'hidden'
                }}>
                {/* Days Header (Only for Desktop) */}
                {!isMobile && (
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                        backgroundColor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 0.5
                    }}>
                        {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day, dayIdx) => (
                            <Box 
                                key={day} 
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '30px',
                                    textAlign: 'center',
                                    backgroundColor: dayIdx >= 5 ? 'action.hover' : 'transparent',
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    '&:last-child': {
                                        borderRight: 'none'
                                    }
                                }}
                            >
                                <Typography variant="subtitle2" sx={{
                                    fontWeight: 700,
                                    // text.secondary rather than primary.main: the labels are
                                    // structure, not an action, and blue read as clickable.
                                    color: dayIdx >= 5 ? 'text.primary' : 'text.secondary',
                                    fontSize: '0.72rem',
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                }}>
                                    {day}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Calendar Days */}
                <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(7, minmax(0, 1fr))' },
                }}>
                    {isMobile ? (
                        // Mobile View: Render each day individually
                        days.map((day, dayIndex) => {
                            const daySchedules = getSchedulesForDate(day);
                            const isToday = isSameDay(day, new Date());

                            return (
                                <Box
                                    key={dayIndex}
                                    sx={{
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: isToday
                                            ? 'action.selected'
                                            : isSameMonth(day, currentDate)
                                                ? 'transparent'
                                                : 'action.hover',
                                        minHeight: '100px',
                                        transition: 'background-color 0.2s ease',
                                        '&:hover': {
                                            bgcolor: 'action.hover'
                                        }
                                    }}
                                    onClick={() => {
                                        setCreationDate(format(day, 'yyyy-MM-dd'));
                                        setDialogOpen(true);
                                    }}
                                >
                                    {/* Day Header for Mobile */}
                                    <Box sx={{ 
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 1,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        backgroundColor: isToday ? 'primary.main' : (isSameMonth(day, currentDate) ? 'transparent' : 'action.disabledBackground')
                                    }}>
                                         <Typography variant="caption" sx={{ 
                                            fontWeight: 500, 
                                            color: isToday ? 'common.white' : (isSameMonth(day, currentDate) ? 'text.primary' : 'text.secondary')
                                        }}>
                                            {format(day, 'EEEE d', { locale: fr })}
                                        </Typography>
                                    </Box>

                                    {/* Schedules for Mobile */}
                                    <Box sx={{ 
                                        p: 1,
                                        overflowY: 'auto',
                                        maxHeight: '200px',
                                         '&::-webkit-scrollbar': {
                                             display: 'none'
                                         },
                                         msOverflowStyle: 'none',
                                         scrollbarWidth: 'none'
                                    }}>
                                        {daySchedules.map(schedule => (
                                            <ScheduleItem key={schedule.id} schedule={schedule} currentDate={day} />
                                        ))}
                                    </Box>
                                </Box>
                            );
                        })
                    ) : (
                        // Desktop View: Render ALL days directly into the 7-column grid (REWRITE START)
                        days.map((day, dayIndex) => {
                            const daySchedules = getSchedulesForDate(day);
                            const isToday = isSameDay(day, new Date());
                            const isLastDayInRow = (dayIndex + 1) % 7 === 0;
                            const isLastRow = dayIndex >= days.length - 7;
                                     
                            return (
                                <Box // Desktop Day Cell
                                    key={dayIndex} 
                                    sx={{
                                        borderRight: isLastDayInRow ? 'none' : '1px solid',
                                        borderBottom: isLastRow ? 'none' : '1px solid',
                                        borderColor: 'divider',
                                        // Weekends get a faint tint so the eye can find the
                                        // week boundary without counting columns.
                                        bgcolor: isToday
                                            ? 'action.selected'
                                            : !isSameMonth(day, currentDate)
                                                ? 'action.disabledBackground'
                                                : [0, 6].includes(day.getDay())
                                                    ? 'action.hover'
                                                    : 'transparent',
                                        opacity: isSameMonth(day, currentDate) ? 1 : 0.55,
                                        minHeight: '120px',
                                        // Reveal per-shift controls only on hover: at three shifts a
                                        // day the delete icons otherwise outnumber the content.
                                        '& .schedule-action': { opacity: 0, transition: 'opacity .15s ease' },
                                        '&:hover .schedule-action': { opacity: 0.75 },
                                        transition: 'background-color 0.2s ease',
                                        '&:hover': {
                                            bgcolor: 'action.hover'
                                        }
                                    }}
                                    onClick={() => {
                                        setCreationDate(format(day, 'yyyy-MM-dd'));
                                        setDialogOpen(true);
                                    }}
                                >
                                    {/* Day Number Box (Desktop) */}
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '26px',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    }}>
                                        <Box sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minWidth: 20,
                                            height: 20,
                                            px: 0.6,
                                            borderRadius: '10px',
                                            // Today is a pill on the number itself rather than a
                                            // full-width bar, which read as a section header.
                                            backgroundColor: isToday ? 'primary.main' : 'transparent',
                                        }}>
                                            <Typography variant="body2" sx={{
                                                color: isToday
                                                    ? 'primary.contrastText'
                                                    : isSameMonth(day, currentDate) ? 'text.primary' : 'text.disabled',
                                                fontWeight: isToday ? 700 : 500,
                                                fontSize: '0.78rem',
                                                lineHeight: 1,
                                                fontVariantNumeric: 'tabular-nums',
                                            }}>
                                                {format(day, 'd')}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Schedules Box (Desktop) */}
                                    <Box sx={{ 
                                        p: 0.5, 
                                        height: 'calc(100% - 25px)', 
                                        overflowY: 'auto',
                                         '&::-webkit-scrollbar': { display: 'none' },
                                         msOverflowStyle: 'none',
                                         scrollbarWidth: 'none'
                                    }}>
                                        {daySchedules.map(schedule => (
                                            <ScheduleItem key={schedule.id} schedule={schedule} currentDate={day} />
                                        ))}
                                    </Box>
                                </Box>
                            );
                        })
                        // Desktop View: Render ALL days directly into the 7-column grid (REWRITE END)
                    )}
                </Box>
                </Paper>
            </Box>

            {/* Footer actions (outside export area) */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={handleExportPdf} startIcon={<PictureAsPdfIcon />} variant="contained" className="hide-on-pdf" sx={{ borderRadius: 2 }}>
                    Exporter en PDF
                </Button>
            </Box>

            {/* Schedule Dialog */}
            <ScheduleDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                auxiliaires={auxiliaires}
                onSave={handleSaveSchedule}
                initialDate={creationDate ?? undefined}
            />

            {/* Schedule Edit Dialog */}
            <ScheduleEditDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                schedule={selectedSchedule}
                auxiliaires={auxiliaires}
                onSave={handleEditSchedule}
            />

            {/* Note Dialog */}
            <NoteDialog
                open={noteDialogOpen}
                onClose={() => {
                    setNoteDialogOpen(false);
                    setNoteSchedule(null);
                }}
                onSave={async (content: string) => {
                    if (!noteSchedule) return;
                    try {
                        await apiClient.post('/api/notes', {
                            schedule_id: noteSchedule.id,
                            content,
                        });
                        setNoteDialogOpen(false);
                        setNoteSchedule(null);
                        // Refresh cache so icons/buttons reflect new count
                        try {
                            const res = await apiClient.get<NoteItem[]>(`/api/schedules/${noteSchedule.id}/notes`);
                            setNotesByScheduleId((prev: Record<number, NoteItem[]>) => ({ ...prev, [noteSchedule.id!]: res.data as NoteItem[] }));
                        } catch {}
                    } catch (error) {
                        console.error('Erreur lors de l\'enregistrement de la note :', error);
                    }
                }}
            />

            <NotesDialog
                open={notesDialogOpen}
                onClose={() => setNotesDialogOpen(false)}
                scheduleId={noteSchedule?.id ?? null}
                onUpdated={(updated) => {
                    if (noteSchedule?.id) {
                        setNotesByScheduleId(prev => ({ ...prev, [noteSchedule.id!]: updated }));
                    }
                }}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog 
                open={deleteDialogOpen} 
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: 400
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>Supprimer le planning récurrent</DialogTitle>
                <DialogContent>
                    <Typography>
                        Ceci est un planning récurrent. Voulez-vous supprimer uniquement cette occurrence ou toutes les occurrences futures ?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 1 }}>
                    <Button 
                        onClick={() => setDeleteDialogOpen(false)}
                        sx={{ borderRadius: 2 }}
                    >
                        Annuler
                    </Button>
                    <Button 
                        onClick={() => {
                            if (selectedSchedule) {
                                handleDeleteSchedule(selectedSchedule.id, false);
                            }
                            setDeleteDialogOpen(false);
                        }}
                        sx={{ borderRadius: 2 }}
                    >
                        Supprimer celle-ci
                    </Button>
                    <Button 
                        onClick={() => {
                            if (selectedSchedule) {
                                handleDeleteSchedule(selectedSchedule.id, true);
                            }
                            setDeleteDialogOpen(false);
                        }}
                        color="error"
                        variant="contained"
                        sx={{ borderRadius: 2 }}
                    >
                        Supprimer toutes les futures
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MonthView; 